import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/config';
import { Table, Button, Modal, Typography, Card, Row, Col, Input, Tag, Space, Avatar, Tooltip } from 'antd';
import { 
  RocketOutlined,
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import StartupRegister from '../../components/auth/StartupRegister';
import RejectReasonModal from '../../components/admin/RejectReasonModal';

const { Title, Text } = Typography;

const StartupManagement = () => {
  const [startups, setStartups] = useState([]);
  const [filteredStartups, setFilteredStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStartup, setEditingStartup] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch startups from API
  const loadStartups = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/startups');
      if (response.data && response.data.success) {
        setStartups(response.data.startups);
        setFilteredStartups(response.data.startups);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch startups');
      }
    } catch (error) {
      console.error('Error loading startups:', error);
      toast.error('Error loading startups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStartups();
  }, []);

  // Search Logic
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    const filtered = startups.filter((startup) => 
      startup.startupName?.toLowerCase().includes(value) ||
      startup.founderName?.toLowerCase().includes(value) ||
      startup.email?.toLowerCase().includes(value) ||
      startup.phone?.includes(value) ||
      startup.industry?.toLowerCase().includes(value)
    );
    setFilteredStartups(filtered);
  };

  const handleReset = () => {
    setSearchText('');
    setFilteredStartups(startups);
  };

  // Toggle verification status
  const handleToggleVerification = async (startupId, currentStatus) => {
    Modal.confirm({
      title: `${currentStatus ? 'Unverify' : 'Verify'} Startup`,
      content: `Are you sure you want to ${currentStatus ? 'unverify' : 'verify'} this startup?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await api.put(`/admin/startups/${startupId}/verify`, {
            isVerified: !currentStatus
          });
          
          if (response.data && response.data.success) {
            toast.success(`Startup ${!currentStatus ? 'verified' : 'unverified'} successfully`);
            loadStartups();
          }
        } catch (error) {
          console.error('Error toggling verification:', error);
          toast.error('Failed to update verification status');
        }
      }
    });
  };

  // Approve / Reject registration status
  const handleApprove = (startupId) => {
    Modal.confirm({
      title: 'Approve Startup Registration',
      content: 'Are you sure you want to approve this startup?',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await api.put(`/admin/startups/${startupId}/approve`);
          if (response.data && response.data.success) {
            toast.success('Startup approved successfully');
            loadStartups();
          }
        } catch (error) {
          toast.error('Failed to approve startup');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleRevert = (startupId) => {
    Modal.confirm({
      title: 'Revert to Pending',
      content: 'This will move the record back to Pending review and it will be hidden from the public site if currently approved. Continue?',
      okText: 'Revert',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await api.put(`/admin/startups/${startupId}/revert`);
          if (response.data && response.data.success) {
            toast.success('Startup reverted to pending');
            loadStartups();
          }
        } catch (error) {
          toast.error('Failed to revert startup');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleReject = (startup) => {
    setRejectTarget(startup);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.put(`/admin/startups/${rejectTarget._id}/reject`, {
        reason: rejectReason
      });
      if (response.data && response.data.success) {
        toast.success('Startup rejected');
        setRejectModalOpen(false);
        loadStartups();
      }
    } catch (error) {
      toast.error('Failed to reject startup');
    } finally {
      setActionLoading(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    setEditingStartup(null);
    setIsModalOpen(true);
  };

  const handleEdit = (startup) => {
    setEditingStartup(startup);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTimeout(() => setEditingStartup(null), 300);
  };

  const handleEditSuccess = () => {
    handleModalClose();
    loadStartups();
  };

  // Delete Startup
  const handleDelete = (startupId) => {
    Modal.confirm({
      title: 'Delete Startup',
      content: 'Are you sure you want to delete this startup? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/startups/${startupId}`);
          
          if (response.data && response.data.success) {
            toast.success('Startup deleted successfully');
            loadStartups();
          } else {
            throw new Error(response.data?.message || 'Failed to delete startup');
          }
        } catch (error) {
          toast.error('Failed to delete startup.');
        }
      }
    });
  };

  // Statistics Calculation
  const stats = {
    total: startups.length,
    verified: startups.filter(s => s.isVerified).length,
    active: startups.filter(s => s.isActive).length,
    inactive: startups.filter(s => !s.isActive).length
  };

  // Styles based on the screenshot
  const cardStyles = {
    total: { bg: '#e6f7ff', color: '#1890ff', icon: <RocketOutlined /> },
    verified: { bg: '#f6ffed', color: '#52c41a', icon: <CheckCircleOutlined /> },
    active: { bg: '#fff7e6', color: '#fa8c16', icon: <CheckCircleOutlined /> },
    inactive: { bg: '#fff1f0', color: '#ff4d4f', icon: <CloseCircleOutlined /> }
  };

  // Table columns
  const columns = [
    {
      title: <span style={{ fontWeight: 500 }}>#</span>,
      key: 'index',
      width: 60,
      render: (text, record, index) => <Text style={{ color: '#666', fontSize:"16px" }}>{index + 1}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Startup Name</span>,
      dataIndex: 'startupName',
      key: 'startupName',
      render: (text, record) => {
        // Construct proper image URL
        let imageUrl = '';
        if (record.logo) {
          if (record.logo.startsWith('http')) {
            imageUrl = record.logo;
          } else if (record.logo.startsWith('/')) {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${record.logo}`;
          } else {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${record.logo}`;
          }
        }
        
        return (
          <Space>
            {record.logo ? (
              <Avatar src={imageUrl} size={40} />
            ) : (
              <Avatar style={{ backgroundColor: '#3C007A' }} size={40}>
                {text?.charAt(0)?.toUpperCase()}
              </Avatar>
            )}
            <div>
              <Text strong style={{ color: '#333', fontSize:"15px", display: 'block' }}>{text}</Text>
              <Text type="secondary" style={{ fontSize:"12px" }}>{record.industry || 'N/A'}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Founder</span>,
      dataIndex: 'founderName',
      key: 'founderName',
      render: (text) => <Text style={{ fontSize:"14px" }}>{text}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Contact Info</span>,
      key: 'contact',
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize:"14px" }}>
            <MailOutlined style={{ marginRight: 5, color: '#1890ff' }} />
            {record.email}
          </Text>
          <Text style={{ fontSize:"14px" }}>
            <PhoneOutlined style={{ marginRight: 5, color: '#52c41a' }} />
            {record.phone}
          </Text>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Stage</span>,
      dataIndex: 'stage',
      key: 'stage',
      render: (stage) => {
        const colors = {
          'Idea': 'blue',
          'Prototype': 'cyan',
          'Seed': 'green',
          'Series A': 'orange',
          'Series B': 'purple',
          'Series C+': 'red'
        };
        return <Tag color={colors[stage] || 'default'}>{stage}</Tag>;
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Status</span>,
      key: 'status',
      render: (record) => {
        const statusColors = { pending: 'gold', approved: 'green', rejected: 'red' };
        return (
          <Space direction="vertical" size={4}>
            <Tag color={statusColors[record.status] || 'default'}>
              {(record.status || 'pending').toUpperCase()}
            </Tag>
            {record.isActive ? (
              <Tag color="blue">Active</Tag>
            ) : (
              <Tag color="red">Inactive</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Created</span>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Text style={{ fontSize:"13px", color: '#666' }}>
          {moment(date).format('MMM DD, YYYY')}
        </Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Actions</span>,
      key: 'actions',
      width: 150,
      render: (record) => (
        <Space size="small">
          {record.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="link"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApprove(record._id)}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="link"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleReject(record)}
                />
              </Tooltip>
            </>
          )}
          {(record.status === 'approved' || record.status === 'rejected') && (
            <Tooltip title="Revert to Pending">
              <Button
                type="link"
                icon={<UndoOutlined />}
                onClick={() => handleRevert(record._id)}
              />
            </Tooltip>
          )}
          <Tooltip title={record.isVerified ? "Unverify" : "Verify"}>
            <Button
              type="link"
              icon={<SafetyCertificateOutlined />}
              onClick={() => handleToggleVerification(record._id, record.isVerified)}
              style={{ color: record.isVerified ? '#52c41a' : '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px', fontFamily: "'Rubik', sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, color: '#333', fontFamily: "'Rubik', sans-serif" }}>
          Startup Management
        </Title>
        <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif" }}>
          Manage all startups in the ecosystem
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.total.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.total.color }}>{cardStyles.total.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.total.color, display: 'block' }}>
                  {stats.total}
                </Text>
                <Text type="secondary">Total Startups</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.verified.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.verified.color }}>{cardStyles.verified.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.verified.color, display: 'block' }}>
                  {stats.verified}
                </Text>
                <Text type="secondary">Verified</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.active.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.active.color }}>{cardStyles.active.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.active.color, display: 'block' }}>
                  {stats.active}
                </Text>
                <Text type="secondary">Active</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.inactive.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.inactive.color }}>{cardStyles.inactive.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.inactive.color, display: 'block' }}>
                  {stats.inactive}
                </Text>
                <Text type="secondary">Inactive</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Search and Filter */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px 8px 0 0', 
        marginBottom: '0px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '200px' }}>
          <Input 
            placeholder="Search startups by name, founder, email, phone, or industry"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '400px', fontFamily: "'Rubik', sans-serif" }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <Text type="secondary" className="hidden md:block">
            Showing {filteredStartups.length} of {startups.length} records
          </Text>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={openAddModal}
            style={{ backgroundColor: '#1890ff', borderRadius: '4px' }}
          >
            Add Startup
          </Button>
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredStartups}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{ 
          pageSize: 10, 
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items` 
        }}
        scroll={{ x: 'max-content' }}
        rowClassName="align-middle"
        style={{ background: '#fff', borderRadius: '0 0 8px 8px' }}
      />

      {/* Edit Modal */}
      <Modal
        title={editingStartup ? 'Edit Startup' : 'Add New Startup'}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={1000}
        centered
        destroyOnClose
      >
        <StartupRegister
          editingStartup={editingStartup}
          isAdminEdit={true}
          onSuccess={handleEditSuccess}
        />
      </Modal>

      <RejectReasonModal
        open={rejectModalOpen}
        title="Reject Startup Registration"
        entityLabel={rejectTarget?.startupName}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onOk={submitRejection}
        onCancel={() => setRejectModalOpen(false)}
        confirmLoading={actionLoading}
      />
    </div>
  );
};

export default StartupManagement;
